import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Typography,
} from "@material-ui/core";
import { Delete } from "@material-ui/icons";
import format from "date-fns/format";
import {
  GetServerDowntimeDocument,
  useCreateServerDowntimeMutation,
  useDeleteServerDowntimeMutation,
  useGetServerDowntimeQuery,
} from "../../../../generated/graphql";
import { IDateRange } from "../../../shared/DateRangePicker/DateRangePicker";
import { ButtonCustom } from "../../../shared/Style/Style";
import { DateRangePicker } from "../../../shared/DateRangePicker";
import { Content } from "../Settings.style";
import { ErrorMessageSnackbar } from "../../../shared/ErrorMessageSnackbar";

const formatDate = (date: string | Date | undefined) => {
  if (!date) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date;
  return format(dateObj, "dd/MM/yyyy HH:mm");
};

export const ScheduledMaintenanceComponent = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const { data, loading } = useGetServerDowntimeQuery();
  const [createServerDowntimeMutation, { loading: createLoading }] =
    useCreateServerDowntimeMutation({
      refetchQueries: [GetServerDowntimeDocument],
    });
  const [deleteServerDowntimeMutation, { loading: deleteLoading }] =
    useDeleteServerDowntimeMutation({
      refetchQueries: [GetServerDowntimeDocument],
    });

  const [isDateRangePickerOpen, setDateRangePickerOpen] = useState(false);

  const isHasDateRange = Boolean(
    data?.getServerDowntime?.dateStart && data?.getServerDowntime?.dateEnd
  );

  const handleChangeDateRange = async ({ startDate, endDate }: IDateRange) => {
    if (!startDate || !endDate) return;

    try {
      await createServerDowntimeMutation({
        variables: {
          dateStart: String(startDate),
          dateEnd: String(endDate),
        },
      });
    } catch (error) {
      const { message } = error as Error;

      if (message.includes("start date must be in the future")) {
        return setErrorMessage("Дата початку повинна бути в майбутньому");
      }
      if (message.includes("start date cannot be after the end date")) {
        return setErrorMessage(
          "Дата початку не може бути після дати закінчення"
        );
      }

      if (message) {
        setErrorMessage(message);
      }
    }
  };

  const handleDeleteDowntime = async () => {
    try {
      await deleteServerDowntimeMutation();
    } catch (error) {
      console.error("Error deleting server downtime:", error);
    }
  };

  return (
    <Content>
      <Box
        height="100%"
        p={3}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <Typography
          variant="h6"
          style={{
            marginBottom: 12,
            textAlign: "center",
            fontWeight: 600,
          }}
        >
          Заплановані технічні роботи
        </Typography>

        <Divider style={{ marginBottom: 24 }} />

        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          fontSize={16}
        >
          {isHasDateRange ? (
            <>
              <div>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gridGap={10}
                  mb={2}
                >
                  <div>
                    <span>
                      <strong>Початок: </strong>
                      {formatDate(data.getServerDowntime.dateStart)}
                    </span>
                    <br />
                    <span>
                      <strong>Кінець: </strong>&nbsp;&nbsp;&nbsp;
                      {formatDate(data.getServerDowntime.dateEnd)}
                    </span>
                  </div>
                  <IconButton
                    onClick={handleDeleteDowntime}
                    disabled={loading || deleteLoading}
                  >
                    <Delete
                      color="secondary"
                      style={{ opacity: loading || deleteLoading ? 0.5 : 1 }}
                    />
                  </IconButton>
                </Box>
                <span>Для зміни запланованого періоду - видаліть поточний</span>
              </div>
            </>
          ) : (
            <span>Зараз немає запланованого періоду</span>
          )}
        </Box>

        <Box display="flex" justifyContent="center" mt="auto">
          <ButtonCustom
            main_color="black"
            isActive={!isHasDateRange}
            style={{ minWidth: 120, fontWeight: 600, fontSize: 16 }}
            disabled={
              isHasDateRange || loading || deleteLoading || createLoading
            }
            onClick={() => setDateRangePickerOpen(true)}
          >
            {loading ? (
              <CircularProgress size={22} style={{ color: "#fff" }} />
            ) : (
              "Створити"
            )}
          </ButtonCustom>
        </Box>

        <DateRangePicker
          withTime
          setDateRange={handleChangeDateRange}
          open={isDateRangePickerOpen}
          onClose={() => setDateRangePickerOpen(false)}
        />
      </Box>
      <ErrorMessageSnackbar
        open={Boolean(errorMessage)}
        message={errorMessage}
        closeHandler={() => setErrorMessage("")}
      />
    </Content>
  );
};
